import {type PointCloudPosition, type PointCloudRgb} from './types';

/**
 * Shared knobs for Babylon and Potree dense draws. Keep visual defaults aligned
 * across libraries; library-specific framing math can still differ.
 */
export const POINT_CLOUD_DRAW = {
  /** App default matches Babylon historical LH Object3D. */
  defaultUseRightHandedSystem: false,

  /** Z-up matches logged Object3D / AV conventions. */
  cameraUp: [0, 0, 1] as const,

  cameraNear: 0.0001,
  cameraFar: 32_000_000,

  /**
   * Babylon ArcRotateCamera initial beta. Potree dense framing approximates the
   * same slight elevation.
   */
  cameraBeta: 1.2,

  /** Card/preview below this edge length skips box labels (Babylon). */
  smallViewMaxEdgePx: 400,

  /**
   * Point size = base * devicePixelRatio * clamp(width / widthRef, min, max).
   */
  pointSizeBase: 3,
  pointSizeWidthRefPx: 200,
  pointSizeWidthFactorMin: 0.4,
  pointSizeWidthFactorMax: 0.8,
  pointSizeKeyboardIncrement: 2,
  pointSizeMin: 1,

  /** Cyan when a vector has no logged color. */
  defaultVectorColor: [0, 255, 255] as PointCloudRgb,

  boxLabelFontSizePx: 16,

  /** Required so card screenshots can read the canvas (Babylon + Potree). */
  preserveDrawingBufferForCard: true,
} as const;

export function pointSizeWidthFactor(width: number): number {
  return Math.min(
    POINT_CLOUD_DRAW.pointSizeWidthFactorMax,
    Math.max(
      POINT_CLOUD_DRAW.pointSizeWidthFactorMin,
      width / POINT_CLOUD_DRAW.pointSizeWidthRefPx
    )
  );
}

export function pointSizeForViewWidth(
  width: number,
  devicePixelRatio = 1
): number {
  return (
    POINT_CLOUD_DRAW.pointSizeBase *
    devicePixelRatio *
    pointSizeWidthFactor(width)
  );
}

export function isSmallPointCloudView(width: number, height: number): boolean {
  const max = POINT_CLOUD_DRAW.smallViewMaxEdgePx;
  return width < max || height < max;
}

/**
 * SDK empty clouds still include one placeholder point at the origin so engines
 * can mount. Treat that as empty for framing / camera targeting.
 */
export function isEmptyPlaceholderPointCloud(
  points: ReadonlyArray<{position: PointCloudPosition}>
): boolean {
  return (
    points.length === 1 &&
    points[0].position[0] === 0 &&
    points[0].position[1] === 0 &&
    points[0].position[2] === 0
  );
}
